import { FileStatusProvider, useFileStatus } from "@portal/providers/FileStatusProvider";
import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { DateTime } from "luxon";
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { type BlockerFunction, useBlocker } from "react-router-dom";
import {
	getEmptyFormFieldPaths,
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../utils/schema-fields";
import { ConfirmationModal } from "../layout/ConfirmationModal";
import { NavigationBlockerModal } from "../layout/NavigationBlockerModal";
import { ShrinkingHeader } from "../layout/ShrinkingHeader";
import { cn } from "../lib/utils";
import { Button } from "../ui/Button";
import { TooltipButton } from "../ui/TooltipButton";
import { trackingAttrs, trackingSegment } from "../utils";
import { MultiStepSummary } from "./MultiStepSummary";
import { PartialSubmitConfirmationModal } from "./PartialSubmitConfirmationModal";
import type {
	AbandonPayload,
	StepCompletedPayload,
	StepCompletedWithErrorsPayload,
	StepConfig,
} from "./types";
import { useMultiStepForm } from "./useMultiStepForm";
import { normalizeFormValues } from "./utils/formResolver";

type SectionNavLayoutProps<TFormData extends FieldValues> = {
	formMethods: UseFormReturn<TFormData>;
	steps: StepConfig<TFormData>[];
	onSuccess: (data: TFormData) => Promise<void>;
	confirmationMessage?: string;
	scrollable?: boolean;
	isSubmitting?: boolean;
	onAbandon?: (payload: AbandonPayload) => void;
	onStepCompleted?: (payload: StepCompletedPayload) => void;
	onStepCompletedWithErrors?: (payload: StepCompletedWithErrorsPayload) => void;
	registerAbandonPayloadGetter?: (getter: (() => AbandonPayload) | null) => void;
	trackingPrefix?: string;
};

const WrappedSectionNavLayout = <TFormData extends FieldValues>({
	formMethods,
	steps,
	onSuccess,
	confirmationMessage,
	scrollable = true,
	isSubmitting = false,
	onAbandon,
	onStepCompleted,
	onStepCompletedWithErrors,
	registerAbandonPayloadGetter,
	trackingPrefix,
}: SectionNavLayoutProps<TFormData>) => {
	const SummaryComponent = useMemo(() => () => <MultiStepSummary steps={steps} />, [steps]);
	const submittingRef = useRef(false);
	const shouldBlock = useCallback<BlockerFunction>(
		({ currentLocation, nextLocation }) => {
			if (submittingRef.current) return false;
			if (isSubmitting) return false;
			if (formMethods.formState.isSubmitting) return false;
			if (currentLocation.pathname === nextLocation.pathname) return false;
			return formMethods.formState.isDirty;
		},
		[formMethods.formState.isDirty, formMethods.formState.isSubmitting, isSubmitting],
	);

	const blocker = useBlocker(shouldBlock);
	// Ref avoids adding blocker to useCallback deps, which would cascade into
	// recreating handleSubmit and re-rendering downstream components on every state change.
	const blockerRef = useRef(blocker);
	blockerRef.current = blocker;

	const allSteps = useMemo<StepConfig<TFormData>[]>(
		() => [
			...steps,
			{
				name: "summary",
				label: "Summary",
				fieldPaths: [],
				component: SummaryComponent,
			},
		],
		[steps, SummaryComponent],
	);

	const {
		currentStep,
		currentStepIndex,
		sectionNavItems,
		goToStep,
		goToNextStep,
		goToPreviousStep,
		isLastStep,
		hasPreviousStep,
	} = useMultiStepForm<TFormData>({ formMethods, steps: allSteps });
	const { isFileStatusPending } = useFileStatus();

	const stepEnteredAtRef = useRef(DateTime.now());
	const stepVisitCountsRef = useRef<Map<string, number>>(new Map());
	const furthestStepIndexRef = useRef(currentStepIndex);
	useEffect(() => {
		furthestStepIndexRef.current = Math.max(furthestStepIndexRef.current, currentStepIndex);
	}, [currentStepIndex]);

	const schema = useFormSchema();

	// ─── Submission state ────────────────────────────────────────────────────
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [partialModalOpen, setPartialModalOpen] = useState(false);
	const pendingDataRef = useRef<TFormData | null>(null);

	const normalizeForSubmit = useCallback((data: unknown): TFormData => {
		return normalizeFormValues(data, {
			returnEmptyObject: true,
			returnEmptyArray: true,
		}) as TFormData;
	}, []);

	const submitData = useCallback(
		async (data: TFormData) => {
			submittingRef.current = true;
			try {
				await onSuccess(data);
				if (blockerRef.current.state === "blocked") {
					blockerRef.current.proceed();
				}
			} catch {
				submittingRef.current = false;
				if (blockerRef.current.state === "blocked") {
					blockerRef.current.reset();
				}
			}
		},
		[onSuccess],
	);

	const onValidSubmit = useCallback(
		async (data: TFormData) => {
			const normalized = normalizeForSubmit(data);
			const isBlockedNav = blockerRef.current.state === "blocked";

			if (schema) {
				const partialPaths = getPartialFieldPaths(schema, normalized);
				const reqForApprovalPaths = getRequiredForApprovalFieldPaths(schema, normalized);
				const allPaths = [...partialPaths, ...reqForApprovalPaths];
				const emptyPaths = allPaths.filter(
					(path) => hasOwnPath(normalized, path) && isEmpty(getNestedValue(normalized, path)),
				);
				if (emptyPaths.length > 0) {
					pendingDataRef.current = normalized;
					// Skip the partial modal when submitting from the nav blocker — submit directly
					if (!isBlockedNav) {
						submittingRef.current = true;
						setPartialModalOpen(true);
						return;
					}
					await submitData(normalized);
					return;
				}
			}
			// Skip the confirmation modal when submitting from the nav blocker
			if (confirmationMessage && !isBlockedNav) {
				pendingDataRef.current = normalized;
				submittingRef.current = true;
				setConfirmModalOpen(true);
			} else {
				await submitData(normalized);
			}
		},
		[schema, confirmationMessage, normalizeForSubmit, submitData],
	);

	const onInvalidSubmit = useCallback(() => {
		submittingRef.current = false;
		setErrorModalOpen(true);
		if (blockerRef.current.state === "blocked") {
			blockerRef.current.reset();
		}
	}, []);

	const confirmSubmit = useCallback(async () => {
		setConfirmModalOpen(false);
		const submission = pendingDataRef.current;
		pendingDataRef.current = null;
		if (submission) {
			await submitData(submission);
		}
	}, [submitData]);

	const confirmPartialSubmit = useCallback(async () => {
		setPartialModalOpen(false);
		const submission = pendingDataRef.current;
		pendingDataRef.current = null;
		if (submission) {
			await submitData(submission);
		}
	}, [submitData]);

	const handleSubmit = useMemo(
		() => formMethods.handleSubmit(onValidSubmit, onInvalidSubmit),
		[formMethods, onValidSubmit, onInvalidSubmit],
	);

	// ─── Scroll / navigation ─────────────────────────────────────────────────
	const [shrunk, setShrunk] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const handleScroll = useCallback(
		(event: UIEvent<HTMLDivElement>) => {
			const scrollTop = event.currentTarget.scrollTop;
			if (scrollTop >= 25) {
				if (!shrunk) setShrunk(true);
			} else if (scrollTop === 0 && shrunk) setShrunk(false);
		},
		[shrunk],
	);

	const scrollToTop = useCallback(() => {
		scrollContainerRef.current?.scrollTo({ top: 0, behavior: "instant" });
	}, []);

	const handleSectionClick = useCallback(
		async (name: string) => {
			if (name === currentStep.name) {
				scrollToTop();
				return;
			}

			if (isFileStatusPending) {
				return;
			}

			const fromStepName = currentStep.name;
			const fromStepIndex = currentStepIndex;
			const enteredAt = stepEnteredAtRef.current;
			const clickedIndex = allSteps.findIndex((s) => s.name === name);
			const isForward = clickedIndex > fromStepIndex;
			const result = await goToStep(name);
			const now = DateTime.now();
			stepEnteredAtRef.current = now;
			if (isForward) {
				const stepVisitCount = (stepVisitCountsRef.current.get(fromStepName) ?? 0) + 1;
				stepVisitCountsRef.current.set(fromStepName, stepVisitCount);
				const basePayload = {
					stepName: fromStepName,
					stepIndex: fromStepIndex,
					durationMs: now.diff(enteredAt).toMillis(),
					via: "sidebar" as const,
					stepVisitCount,
				};
				if (result.ok) {
					onStepCompleted?.(basePayload);
				} else {
					onStepCompletedWithErrors?.({
						...basePayload,
						errorFieldPaths: result.errorFieldPaths,
					});
				}
			}
			scrollToTop();
		},
		[
			currentStep.name,
			currentStepIndex,
			allSteps,
			isFileStatusPending,
			goToStep,
			onStepCompleted,
			onStepCompletedWithErrors,
			scrollToTop,
		],
	);

	const handleNext = useCallback(async () => {
		const fromStepName = currentStep.name;
		const fromStepIndex = currentStepIndex;
		const enteredAt = stepEnteredAtRef.current;
		const result = await goToNextStep();
		const now = DateTime.now();
		stepEnteredAtRef.current = now;
		const stepVisitCount = (stepVisitCountsRef.current.get(fromStepName) ?? 0) + 1;
		stepVisitCountsRef.current.set(fromStepName, stepVisitCount);
		const basePayload = {
			stepName: fromStepName,
			stepIndex: fromStepIndex,
			durationMs: now.diff(enteredAt).toMillis(),
			via: "next" as const,
			stepVisitCount,
		};
		if (result.ok) {
			onStepCompleted?.(basePayload);
		} else {
			onStepCompletedWithErrors?.({
				...basePayload,
				errorFieldPaths: result.errorFieldPaths,
			});
		}
		scrollToTop();
	}, [
		currentStep.name,
		currentStepIndex,
		goToNextStep,
		onStepCompleted,
		onStepCompletedWithErrors,
		scrollToTop,
	]);

	const handleBack = useCallback(async () => {
		await goToPreviousStep();
		stepEnteredAtRef.current = DateTime.now();
		scrollToTop();
	}, [goToPreviousStep, scrollToTop]);

	const buildAbandonPayload = useCallback((): AbandonPayload => {
		const furthestStepIndex = furthestStepIndexRef.current;
		const emptyFieldPaths = schema ? getEmptyFormFieldPaths(schema, formMethods.getValues()) : [];
		return {
			lastStepName: currentStep.name,
			lastStepIndex: currentStepIndex,
			furthestStepName: allSteps[furthestStepIndex]?.name ?? currentStep.name,
			furthestStepIndex,
			emptyFieldPaths,
		};
	}, [currentStep.name, currentStepIndex, allSteps, schema, formMethods]);

	useEffect(() => {
		registerAbandonPayloadGetter?.(buildAbandonPayload);
		return () => registerAbandonPayloadGetter?.(null);
	}, [buildAbandonPayload, registerAbandonPayloadGetter]);

	const StepComponent = currentStep.component;

	return (
		<>
			<div
				onScroll={handleScroll}
				className={cn(
					"transition-all ease-linear w-full grid px-[65px] scrollbar-gutter-stable-both-edges",
					"grid-cols-[1fr_minmax(min-content,710px)_minmax(50px,75px)_130px_1fr]",
					scrollable && "overflow-y-auto h-full",
				)}
				style={{ gridTemplateRows: `${shrunk ? 55 : 85}px 1fr` }}
				ref={scrollContainerRef}
			>
				{/* Row 1: header spans content column */}
				<div className="col-start-1 row-span-2" />
				<ShrinkingHeader shrunk={shrunk} title={currentStep.label} />
				<div className="col-start-3 row-span-2" />

				{/* Sidebar: nav + buttons, sticky */}
				<div className="col-start-4 row-span-2 sticky top-0 left-0 pt-[30px] flex flex-col space-y-[15px] self-start">
					<div className="w-full bg-gray-50 rounded-[6px] p-[8px] min-h-fit">
						{sectionNavItems.map((section) => (
							<div
								key={section.name}
								data-testid={`section-nav-${section.name}`}
								{...trackingAttrs(
									trackingPrefix
										? `${trackingPrefix}-sidebar-${trackingSegment(section.name)}`
										: undefined,
								)}
								className={cn(
									"w-full flex items-center cursor-pointer hover:bg-gray-100 text-[14px] h-[34px] px-[10px] select-none",
									section.name === currentStep.name
										? "bg-gray-100 text-primary font-medium"
										: "text-secondary font-normal",
									section.severity === "error" && "text-red-500",
								)}
								onClick={() => handleSectionClick(section.name)}
							>
								{section.label}
							</div>
						))}
					</div>
					<div className="flex flex-col w-full space-y-[5px] min-h-fit">
						{!isLastStep && (
							<TooltipButton
								tabIndex={2}
								color="primary"
								filled
								onClick={handleNext}
								disabled={isFileStatusPending}
								tooltip="Wait for file to finish before navigating"
								trackingId={trackingPrefix ? `${trackingPrefix}-next` : undefined}
							>
								Next
							</TooltipButton>
						)}
						{isLastStep && (
							<>
								<Button
									tabIndex={2}
									color="confirm"
									filled
									loading={isSubmitting}
									onClick={handleSubmit}
									trackingId={trackingPrefix ? `${trackingPrefix}-submit` : undefined}
								>
									Submit
								</Button>
							</>
						)}
						{hasPreviousStep && (
							<Button
								tabIndex={2}
								color="inverse"
								filled
								className="hover:brightness-100"
								onClick={handleBack}
								trackingId={trackingPrefix ? `${trackingPrefix}-back` : undefined}
							>
								Back
							</Button>
						)}
					</div>
				</div>

				<div className="col-start-5 row-span-2" />

				{/* Content area */}
				<div className="col-start-2 w-full h-full px-[15px]">
					<StepComponent />
				</div>
			</div>
			{/* Submission modals */}
			{blocker.state === "blocked" && (
				<NavigationBlockerModal
					blocker={blocker}
					message="You're navigating away from the page. Would you like to save your work?"
					confirmButtonText="Yes, Save & Submit to inRev"
					cancelButtonText="No, My work will be lost"
					isSubmitting={isSubmitting || formMethods.formState.isSubmitting}
					onCancel={() => {
						onAbandon?.(buildAbandonPayload());
						blocker.proceed();
					}}
					onConfirm={() => {
						if (submittingRef.current) return;
						submittingRef.current = true;
						handleSubmit();
					}}
					confirmationButtonColor="confirm"
					cancelButtonColor="danger"
					trackingPrefix={trackingPrefix}
				/>
			)}
			{confirmModalOpen && (
				<ConfirmationModal
					message={confirmationMessage}
					confirmButtonText="Submit"
					cancelButtonText="Cancel"
					confirmTrackingId={trackingPrefix ? `${trackingPrefix}-confirm-submit` : undefined}
					cancelTrackingId={trackingPrefix ? `${trackingPrefix}-confirm-cancel` : undefined}
					onConfirm={confirmSubmit}
					onCancel={() => {
						submittingRef.current = false;
						setConfirmModalOpen(false);
					}}
				/>
			)}
			{errorModalOpen && (
				<ConfirmationModal
					message="Please complete the required fields before submitting."
					confirmButtonText="OK"
					confirmTrackingId={trackingPrefix ? `${trackingPrefix}-validation-ok` : undefined}
					onConfirm={() => {
						submittingRef.current = false;
						setErrorModalOpen(false);
					}}
					cancelButton={false}
				/>
			)}
			{partialModalOpen && (
				<PartialSubmitConfirmationModal
					header="Ineligible for Auto-Approval"
					message="Some information is missing, so this submission will be referred to an underwriter for review."
					submessage="After submitting, you can provide additional information on the Account page."
					onClose={() => {
						submittingRef.current = false;
						setPartialModalOpen(false);
					}}
					onSubmit={confirmPartialSubmit}
					trackingPrefix={trackingPrefix}
				/>
			)}
		</>
	);
};

export const SectionNavLayout = <TFormData extends FieldValues>(
	props: SectionNavLayoutProps<TFormData>,
) => {
	return (
		<FileStatusProvider>
			<WrappedSectionNavLayout {...props} />
		</FileStatusProvider>
	);
};
